import type { AdminDashboardLocale } from "./admin-dashboard-locale";

export type AdminDashboardLocaleText<T> = {
  en: T;
  ko: T;
};

export function pickAdminDashboardLocaleText<T>(
  locale: AdminDashboardLocale,
  text: AdminDashboardLocaleText<T>,
): T {
  return locale === "ko" ? text.ko : text.en;
}

export function formatAdminDashboardLocaleLabel(
  locale: AdminDashboardLocale,
): string {
  return pickAdminDashboardLocaleText(locale, {
    en: "Language",
    ko: "언어",
  });
}

export function formatAdminDashboardLocaleOptionLabel(
  locale: AdminDashboardLocale,
  option: AdminDashboardLocale,
): string {
  return pickAdminDashboardLocaleText(locale, {
    en: option === "ko" ? "Korean" : "English",
    ko: option === "ko" ? "한국어" : "영어",
  });
}

export type AdminDashboardSummaryKey =
  | "app"
  | "correlation"
  | "device"
  | "error"
  | "internalUserId"
  | "platform"
  | "release"
  | "request"
  | "user"
  | "userIdHash";

export function formatAdminDashboardSummaryKey(
  locale: AdminDashboardLocale,
  key: AdminDashboardSummaryKey,
): string {
  switch (key) {
    case "app":
      return pickAdminDashboardLocaleText(locale, { en: "App", ko: "앱" });
    case "correlation":
      return pickAdminDashboardLocaleText(locale, { en: "Correlation", ko: "상관관계" });
    case "device":
      return pickAdminDashboardLocaleText(locale, { en: "Device", ko: "기기" });
    case "error":
      return pickAdminDashboardLocaleText(locale, { en: "Error", ko: "오류" });
    case "internalUserId":
      return pickAdminDashboardLocaleText(locale, { en: "Internal ID", ko: "내부 ID" });
    case "platform":
      return pickAdminDashboardLocaleText(locale, { en: "Platform", ko: "플랫폼" });
    case "release":
      return pickAdminDashboardLocaleText(locale, { en: "Release", ko: "릴리스" });
    case "request":
      return pickAdminDashboardLocaleText(locale, { en: "Request", ko: "요청" });
    case "user":
      return pickAdminDashboardLocaleText(locale, { en: "User", ko: "사용자" });
    case "userIdHash":
      return pickAdminDashboardLocaleText(locale, { en: "User Hash", ko: "사용자 해시" });
  }
}

export function formatAdminDashboardSummary(
  locale: AdminDashboardLocale,
  entries: ReadonlyArray<{ key: AdminDashboardSummaryKey; value: string | null | undefined }>,
): string | undefined {
  const visibleEntries = entries.filter(
    (entry): entry is { key: AdminDashboardSummaryKey; value: string } =>
      typeof entry.value === "string" && entry.value.length > 0,
  );

  if (visibleEntries.length === 0) {
    return undefined;
  }

  return visibleEntries
    .map((entry) => `${formatAdminDashboardSummaryKey(locale, entry.key)}=${entry.value}`)
    .join(", ");
}
